import cn from 'classnames';
import { useState, useEffect } from 'react';

import {
  useCheckoutContext,
  CartMessages,
  AlertMessage,
  PlaceOrderMessage,
} from '@zooplus/checkout-state';

import RestoreArticle from '../../../cart/restore-article';
import AlertComponent from '../alert-component';

import { useTranslation } from '@hooks/use-translations';
import { MESSAGE_PLACEMENT, CHECKOUT_STATE_PROJECT_ID, MESSAGE_CODE } from '@utilities/constants';
import { HTMLParser } from '@utilities/html-parser';
import { isObjectNotNullOrEmpty, isArrayNotEmpty } from '@utilities/utils';

import FeedbackComponent from './feedback-component';
import styles from './feedback-message.module.scss';

type FeedbackMessageProps = {
  placement: string;
  customStyle?: string;
  containerStyle?: string;
  disableOverviewCallback?: Function;
  isEmailExisting?: boolean;
  shipmentId?: number;
};

const FeedbackMessage = ({
  placement,
  customStyle,
  containerStyle,
  disableOverviewCallback,
  isEmailExisting,
  shipmentId,
}: FeedbackMessageProps) => {
  const { t } = useTranslation();
  const {
    checkoutState,
    prevArticle,
    isEditStepTriggered,
    setIsEditStepTriggered,
    setCoupon,
    deleteCoupon,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  const [isActive, setIsActive] = useState<boolean>(false);
  const [showRestoreArticleMessage, setShowRestoreArticleMessage] = useState<boolean>(false);

  const cartMessages = checkoutState?.cart?.cartMessages;
  const articles = checkoutState?.cart?.articles;
  const couponResult = checkoutState?.couponResult;
  const stateMessages = checkoutState?.stateMessages;

  const hasCartMessages: boolean = isArrayNotEmpty(cartMessages as CartMessages[]);
  const articlesLength: number = articles?.length || 0;

  useEffect(() => {
    hasCartMessages && setIsActive(true);
    !hasCartMessages && disableOverviewCallback && disableOverviewCallback(false);
  }, [hasCartMessages, disableOverviewCallback]);

  useEffect(() => {
    if (isObjectNotNullOrEmpty(prevArticle) && articlesLength > 1) {
      setIsActive(true);
      setShowRestoreArticleMessage(true);
    }
  }, [prevArticle, articlesLength]);

  useEffect(() => {
    if (isArrayNotEmpty(cartMessages as CartMessages[]) && !!disableOverviewCallback) {
      disableOverviewCallback(
        cartMessages?.some(
          (currentValue: { blockedOnOverview: boolean }) => currentValue?.blockedOnOverview,
        ),
      );
    }
  }, [cartMessages, disableOverviewCallback]);

  const handleCouponAddition = (couponCode?: string) => {
    couponCode && setCoupon?.({ couponCode });
  };

  const handleCouponRemoval = (couponCode?: string) => {
    couponCode && deleteCoupon?.({ couponCode });
  };

  return (
    <div className={cn(styles.messagesContainer, containerStyle)}>
      {isArrayNotEmpty(cartMessages as CartMessages[]) &&
        cartMessages?.map((message, index) => {
          if (
            [
              MESSAGE_PLACEMENT.SHIPMENT_CONTAINER_HEADER,
              MESSAGE_PLACEMENT.SHIPMENT_DELIVERY_OPTIONS,
            ].includes(placement) &&
            message?.placements.includes(placement)
          ) {
            if (shipmentId === message.shipmentId) {
              return (
                <AlertComponent
                  key={index}
                  variantType="error"
                  dataTestId={`cart-message-shipment-${index}`}
                  customStyle={cn(customStyle, styles.commonStyle)}
                  message={<HTMLParser content={message.localizedMessage} />}
                />
              );
            }
          } else {
            return (
              message?.placements.includes(placement) &&
              message?.code !== MESSAGE_CODE.FOD_CAPPING_REACHED && (
                <FeedbackComponent
                  key={index}
                  message={message}
                  index={`cart-message-${index.toString()}`}
                  dataTestId={`cart-message-${message.type.toLowerCase()}`}
                  customStyle={customStyle}
                  isActive={isActive}
                  setIsActive={setIsActive}
                  couponResult={couponResult}
                  handleCouponAddition={handleCouponAddition}
                  handleCouponRemoval={handleCouponRemoval}
                />
              )
            );
          }
          return null;
        })}

      {Object.values(stateMessages || {})
        .filter(Array.isArray)
        .map(
          (message: AlertMessage[] | PlaceOrderMessage[]) =>
            isArrayNotEmpty(message) &&
            message.map(
              (msg, index) =>
                msg.placements?.includes(placement) &&
                (msg.code !== MESSAGE_CODE.CHECKOUT_PREVIEW_CUSTOMER_EXISTING_EMAIL ||
                  isEmailExisting) && (
                  <FeedbackComponent
                    key={`state-message-${index.toString()}`}
                    message={msg}
                    index={`state-message-${index.toString()}`}
                    dataTestId={`message-${msg?.uiType}`}
                    customStyle={cn(customStyle, styles.alertAnchor)}
                    isActive={isActive}
                    setIsActive={setIsActive}
                  />
                ),
            ),
        )}

      {couponResult?.errorOccurred && placement === MESSAGE_PLACEMENT.COUPON && (
        <AlertComponent
          key="coupon-addition-error"
          variantType="info"
          dataTestId="coupon-addition-error-msg"
          customStyle={cn(customStyle, styles.commonStyle)}
          message={
            <HTMLParser
              content={t('cart.feedback.coupon.added.error', { 0: couponResult.couponCode })}
            />
          }
        />
      )}

      {placement === MESSAGE_PLACEMENT.CART_EMPTY_PAGE_HEADER && (
        <AlertComponent
          key="empty-cart-restore-article"
          variantType="info"
          dataTestId="removed-article-msg"
          customStyle={cn(customStyle, styles.commonStyle)}
          message={
            <>
              <HTMLParser content={t('cart.item.removed.message')} />
              <RestoreArticle />
            </>
          }
        />
      )}

      {placement === MESSAGE_PLACEMENT.CART_OVERVIEW_BOTTOM_RIGHT && showRestoreArticleMessage && (
        <AlertComponent
          key="cart-overview-restore-article"
          variantType="info"
          dataTestId="removed-article-msg"
          hideAlert
          showCloseButton
          showActionButton
          isActive={showRestoreArticleMessage}
          setActiveState={setShowRestoreArticleMessage}
          customStyle={cn(customStyle, styles.commonStyle, styles.snackbarAlert)}
          message={<HTMLParser content={t('cart.item.removed.message')} />}
        />
      )}

      {placement === MESSAGE_PLACEMENT.PREVIEW_BOTTOM_RIGHT && isEditStepTriggered && (
        <AlertComponent
          key="preview-unsaved-changes-alert"
          variantType="caution"
          dataTestId="unsaved-changes-alert"
          showCloseButton
          isActive={isEditStepTriggered}
          setActiveState={setIsEditStepTriggered}
          customStyle={cn(styles.commonStyle, styles.snackbarAlert, styles.unsavedChangesAlert)}
          message={<HTMLParser content={t('checkout.preview.unsaved.change.alert')} />}
        />
      )}
    </div>
  );
};

export default FeedbackMessage;
